import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useConsent } from '../context/ConsentContext';
import { MEASUREMENT_ID, setAnalyticsTrackingEnabled, trackEvent, trackPageView } from '../utils/analytics';
import { getCspNonce } from '../utils/csp';

const deniedConsent = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

export default function Analytics() {
  const location = useLocation();
  const { canUse } = useConsent();
  const analyticsEnabled = canUse('analytics');

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag('consent', 'default', { ...deniedConsent, wait_for_update: 500 });
    window.gtag('js', new Date());
    window.gtag('config', MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });

    if (!document.getElementById('google-gtag-script')) {
      const script = document.createElement('script');
      script.id = 'google-gtag-script';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
      const nonce = getCspNonce();
      if (nonce) script.nonce = nonce;
      document.head.appendChild(script);
    }

    // Consent Mode governs storage. Tracking stays enabled for anonymous, cookieless pings.
    setAnalyticsTrackingEnabled(true);
  }, []);

  useEffect(() => {
    window.gtag?.('consent', 'update', {
      ...deniedConsent,
      analytics_storage: analyticsEnabled ? 'granted' : 'denied',
    });
  }, [analyticsEnabled]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      trackPageView({
        pageTitle: document.title,
        pagePath: `${location.pathname}${location.search}`,
        pageLocation: window.location.href,
      });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (event) => {
      if (typeof event.target?.closest !== 'function') return;
      const link = event.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      const params = {
        link_text: link.textContent?.trim(),
        link_url: href,
        page_path: `${window.location.pathname}${window.location.search}`,
      };

      if (href.startsWith('tel:')) trackEvent('phone_click', params);
      else if (href.startsWith('sms:')) trackEvent('sms_click', params);
      else if (href.includes('wa.me') || href.includes('whatsapp')) trackEvent('whatsapp_click', params);
      else if (href.includes('instagram.com')) trackEvent('social_click', { ...params, social_network: 'instagram' });
      else if (href.includes('facebook.com')) trackEvent('social_click', { ...params, social_network: 'facebook' });
      else if (href.includes('g.page') && href.includes('/review')) trackEvent('review_click', params);
      else if (href.includes('google.com/maps')) trackEvent('map_open', params);
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return null;
}
